export const distributionPlannerEn = {
  pageTitle: "Distribution Planner",
  pageSubtitle:
    "Review how many stores range each SKU, set min/max store targets and re-balance distribution across the fleet",

  category: {
    title: "Category",
    placeholder: "category",
    prompt: "Select a category to start planning distribution",
    loading: "Loading categories...",
    loadFailed: "Unable to load categories.",
    invalid: "Invalid category.",
  },

  status: {
    loading: "Loading distribution...",
    loadError: "Unable to load the distribution plan.",
  },

  actions: {
    saveTargets: "Save targets",
    restoreEtl: "Restore last ETL ranging",
    runDistribution: "Run distribution",
  },

  readOnly: {
    hint: "Read-only view: you can filter and export this plan. Editing targets and running the distribution need the inputs-update permission.",
  },

  categoriesEntry: {
    button: "Distribution",
  },

  priority: {
    label: "Run priority:",
    core: "Core range",
    minMax: "Min/max store targets",
    spaceMix: "Space mix targets",
    hint: "Earlier steps win when constraints conflict: core range is placed first and never removed, min/max store counts bind next, and space-mix targets steer only the shelf left over.",
  },

  tiles: {
    storeFleet: "Store fleet",
    storeFleetHint: "Stores in this category",
    storesWithoutFixtures: "{{count}} more linked stores have no fixtures and are excluded",
    masterList: "Master list",
    masterListHint: "Products in scope",
    reservedSpace: "{{count}} more hold space for other categories and are excluded",
    excluded: "{{count}} more are excluded from planning",
    withinTarget: "Within target",
    belowMin: "Below min",
    aboveMax: "Above max",
    noTarget: "No target set",
  },

  spaceMix: {
    title: "Space mix",
    subtitle:
      "Share of category sales by segment today, against the mix you would like to plan towards. Targets are saved with the category and override the next distribution run; the selector below decides whether a target is one fleet-wide total or the same share in every store. Segments without a target keep following each store's own sales mix.",
    mode: {
      label: "How targets apply",
      network: "Network total, tilted per store",
      uniform: "Uniform share in every store",
      explainer:
        "Network total: the target is a fleet-wide share of shelf. The run tilts each store's own sales mix up or down until the total across all stores lands on the target, so store-to-store demand differences survive. Uniform: every store's shelf is planned with exactly the target share — one identical mix across the whole fleet, at the cost of local demand differences. Per-cluster targets are not supported.",
    },
    segment: "Segment",
    variant: "Variant",
    level: {
      label: "Level",
      cdt0: "Variant (CDT0)",
      cdt1: "Segment (CDT1)",
      explainer:
        "Variant: one target per CDT0 value — the share of the whole shelf the variant should hold. Segment: one target per CDT1 value within its variant, also as a share of the whole shelf, the way the planogram editor's space-mix panel lists CDT1 rows. The two levels combine: a variant target sets the variant's total and its segment targets split that total; a segment target in an untargeted variant pins just that segment.",
    },
    inheritedTarget:
      "Derived from the variant's target: this segment takes its share of the variant's total following current sales. Type a value to pin this segment within the variant.",
    cellTargets: "{{count}} segment target(s) set",
    cellTargetsHint:
      "Segment targets set at the CDT1 level: this variant's planned share is their sum plus its other segments' share of the residual.",
    skus: "{{count}} SKUs",
    currentShare: "Current share",
    currentShareExplainer:
      "A segment's current share is its sales-based return on space: how much of the category's sales the segment's SKUs bring in.",
    equationLhs: "Current share (%)",
    equationNumerator: "Σ sales of the segment's SKUs across all stores",
    equationDenominator: "Σ sales of every SKU in the category across all stores",
    equationNote:
      "Sales are summed across every store, whether or not a SKU is currently ranged there — the share measures demand and does not move when distribution changes.",
    targetShare: "Target %",
    targetShareExplainerUniform:
      "Targets apply uniformly: every store is planned to the same segment share, replacing that store's own sales mix. A store that over- or under-indexes on the segment gets the same target, so treat it as a deliberate chain-wide override rather than a demand forecast. Per-SKU min/max store limits still bind, so realized shares will differ store by store.",
    targetShareExplainerNetwork:
      "Targets apply as a network total: the run tilts each store's own sales mix by a common factor until the capacity-weighted total across all stores hits the target. Stores that over-index on the segment stay above it and under-indexing stores stay below — only the fleet total lands on the number. Per-SKU min/max store limits still bind.",
    delta: "Delta",
    onTarget: "On target",
    points: "{{value}} pt",
    totalAllocated: "Total allocated",
    ofHundred: "of 100%",
    reset: "Reset to current mix",
    reservedNote:
      "Min-store targets, core-range SKUs and mandatory per-store coverage reserve ~{{reserved}}% of total shelf — space-mix targets can only steer the remaining ~{{free}}%.",
    reservedNoteFillable:
      "Min-store targets, core-range SKUs and mandatory per-store coverage reserve ~{{reserved}}% of the fillable shelf — space-mix targets can only steer the remaining ~{{free}}%.",
    fillableNote:
      "Every eligible SKU ranged in every eligible store fills only ~{{fillable}}% of the fixture width. The shelf cannot be filled whatever the targets say, so every share here is of that fillable part.",
    fillableExplainer:
      "The catalogue is measured the way the run packs it: each SKU's width in each store it may be ranged in, at the system-defined average facing, within its max-store limit. When that adds up to less than the fixtures, the run leaves the rest empty — more SKUs, more eligible stores or wider facings are the only remedies, not different targets. Judging targets against the whole fixture would flag every target that sums to 100%, so the reachable and reserved shares are expressed against the shelf the run can actually fill.",
    reservedExplainer:
      "A min-store target is a store count, but each of those stores must give the SKU physical space: its shelf width × a system-defined average facing. Core-range SKUs count the same way — each one is a guaranteed placement in its store, ranged before anything else. Every store must also carry at least one SKU of every segment, so per store the narrowest eligible SKU of each segment is reserved too — or the core SKUs already placed there. A segment's floor is the larger of its minimums-and-core and that coverage; summing the floors, divided by the fleet's total shelf capacity, gives the reserved share. It is a fleet-wide aggregate — individual stores can be committed more or less than this.",
    unreachableCatalogue:
      "Unreachable target: every SKU of {{segment}} ranged in every eligible store fills at most about {{max}}% of the shelf. No target can add product — more SKUs, more eligible stores or wider facings can.",
    unreachableMaxStores:
      "Unreachable target: the max-store limits on {{segment}}'s SKUs cap it at about {{max}}% of the shelf.",
    unreachableOtherSegments:
      "Unreachable target: the shelf other segments' minimums, core range and coverage reserve leaves {{segment}} at most about {{max}}% of the shelf.",
    forcedLowTarget:
      "Already above target: {{segment}}'s own minimums, core range and per-store coverage reserve about {{min}}% of shelf, so the run cannot go lower.",
    noData: "No segments to show for this category.",
    unassigned: "Unassigned",
    automaticTarget:
      "Automatic: no target set. This segment takes its share of whatever the explicit targets leave, following current sales.",
    automaticLabel: "Auto",
    feasibilityStale:
      "Feasibility reflects the last saved min/max targets — {{count}} unsaved product edit(s) are not included until you save.",
    allocation: {
      automatic: "No explicit targets: 100% is automatic, following each store's demand.",
      over: "Explicit targets sum to {{total}}% — above 100%. The run scales them all down proportionally (their relative sizes survive), and segments without a target get no share of their own.",
      underComplete:
        "Every segment has an explicit target but they sum to {{total}}% — below 100%. The leftover has nowhere to flow, so the run scales the targets up proportionally to fill the shelf.",
      underPartial:
        "Explicit targets sum to {{total}}%. The remaining {{rest}}% flows to the {{count}} segment(s) without a target, following each store's own sales mix.",
    },
    clusters: {
      networkTab: "All clusters",
      networkHint: "network total",
      storeCount: "{{count}} stores",
      unclustered: "No cluster",
      targetShare: "Target %",
      clusterShare: "Cluster share",
      simInput: "Simulate sales (± pt)",
      adjustedShare: "Adjusted share",
      networkNote:
        "While a simulation is active the target is the sales-weighted roll-up of your per-cluster simulated mix; otherwise it shows the saved targets, or today's share where none is saved. Open a cluster tab to simulate updated segment sales; saving persists the roll-up as the targets.",
      clusterNote:
        "Pick + or − and enter percentage points to simulate this segment's updated sales in this cluster. The adjusted segments hold their new share and the rest of the cluster rebalances to 100%. Saving persists the resulting network target.",
    },
  },

  scatter: {
    title: "Distribution vs sales per store",
    subtitle:
      "Each point is a SKU in the current filter. Reference lines mark the median of the visible SKUs.",
    xAxis: "Stores ranged",
    yAxis: "Avg sales / store",
    hintExpand: "high sales/store, low distribution — expand?",
    hintCut: "low sales/store, high distribution — cut?",
    tooltipDistribution: "Distribution",
    tooltipAvgSales: "Avg sales / store",
    tooltipStores: "{{count}} stores",
    noData: "No SKUs match the current filters.",
    legendNoTarget: "No target",
    legendOk: "Within target",
    legendBelow: "Below min",
    legendAbove: "Above max",
  },

  table: {
    title: "Products",
    searchPlaceholder: "Search name, brand or product code",
    allOf: "All {{name}}",
    coreAll: "All SKUs",
    coreOnly: "Core range only",
    coreNon: "Non-core only",
    statusAll: "All statuses",
    statusNone: "No target set",
    statusOk: "Within target",
    statusBelow: "Below min",
    statusAbove: "Above max",
    statusReserved: "Reserved space",
    statusExcluded: "Excluded",
    feedAll: "Any feed",
    feedMissing: "Missing from latest feed",
    results: "{{count}} of {{total}} SKUs",
    dirtyHint: "{{count}} unsaved min/max edits",
    loading: "Loading products...",
    noRows: "No products match the current filters.",
    yes: "Yes",
    none: "—",
    exportCsv: "Export CSV",
    importCsv: "Import CSV",
    importCsvHint: "Import min/max targets by product code",
  },

  columns: {
    product: "Product",
    core: "Core",
    avgSales: "Avg sales/store",
    distribution: "Distribution",
    lastInFeed: "Last in feed",
    min: "Min",
    max: "Max",
    status: "Status",
  },

  statusLabels: {
    ok: "Within target",
    below: "Below min",
    above: "Above max",
    reserved: "Reserved space",
    excluded: "Excluded",
    none: "No target",
  },

  bulk: {
    hint: "Tick the checkboxes in the first column (or the header checkbox) to set min/max store targets for many SKUs at once.",
    selected: "{{count}} SKUs selected",
    min: "Set min",
    max: "Set max",
    setMin: "Set min",
    setMax: "Set max",
    clearMinMax: "Clear min/max",
    markReserved: "Mark as reserved space",
    markReservedHint:
      "For items that stand for space set aside for another category. The run leaves them as they are and takes their width off the shelf.",
    unmarkReserved: "Unmark reserved space",
    reservedSkipped:
      "Min/max changes skip the {{count}} reserved-space or excluded rows in the selection",
    exclude: "Exclude from planning",
    excludeHint:
      "For delisted items, such as products a later feed left out. The run never ranges them and takes them out of any store where they are still ranged.",
    include: "Include again",
    clearSelection: "Clear selection",
  },

  run: {
    title: "Run distribution",
    subtitle: "Recompute store assortments for this category",
    explanation:
      "Recomputes every SKU's store assortment for this category. Core-range SKUs are placed first and never removed; then each SKU's min/max store targets, fixture capacity, variant coverage and your saved space-mix targets steer the rest — targets applied per your saved mode: uniformly in every store, or as a network total tilted per store. This rewrites live store assortments.",
    skus: "SKUs",
    storeFleet: "Store fleet",
    withTargets: "With min/max set",
    reservedSpace:
      "{{count}} items hold space for other categories. The run leaves their ranging as it is and takes about {{width}} of shelf per store out of the space it fills.",
    reservedSpacePending:
      "{{count}} reserved-space or exclusion changes are not saved yet. The run saves them first, which changes the shelf and the products it fills it with, so the capacity estimate is shown again once they are saved.",
    excluded:
      "{{count}} items are excluded from planning. The run never ranges them and takes them out of any store where they are still ranged.",
    warningTitle: "This changes live store assortments",
    warningBody:
      "Store counts are decided by the run, not entered here. Every SKU in the category is reallocated, so distribution can go up or down for any of them.",
    savesDirtyTargets:
      "{{count}} unsaved min/max edits will be saved first, because the run reads the persisted targets.",
    savesDirtyMix:
      "Unsaved space-mix targets will be saved first — the run uses them as segment overrides.",
    skipsStoresWithoutFixtures:
      "{{count}} linked stores have no fixture width and will be skipped — their assortment stays as it is.",
    preferCurrentAssortment: "Prefer current assortment (low churn)",
    preferCurrentAssortmentOn:
      "Today's ranging is the starting point: every SKU keeps its stores where it still fits and its max store target allows; sales only decide what fills the remaining space. A min store target that needs more space than is free is reported, not forced.",
    preferCurrentAssortmentOff:
      "Sales re-decide the whole assortment: a stronger unranged SKU can displace a weaker ranged one. Expect more store adds and removals.",
    facingsTitle: "Facings per SKU",
    facingsHint:
      "How much shelf run one SKU is costed at, in units of its own width. An average across SKUs, so decimals are normal.",
    facingsDecrease: "Decrease facings per SKU",
    facingsIncrease: "Increase facings per SKU",
    facingsMeasured:
      "Latest planograms for this category measure {{facings}} across {{stores}} stores.",
    facingsNoMeasurement:
      "No finished planograms to measure yet — the default is one facing per SKU.",
    facingsUseMeasured: "Use measured",
    facingsCapacity:
      "Fixtures hold about {{skus}} SKUs per store at this setting ({{total}} placements fleet-wide).",
    facingsBelowCurrent:
      "Below today's average of {{ranged}} ranged SKUs per store: with the current assortment preferred, kept rows will be trimmed to fit.",
    facingsUnsaved: "The new facings value is saved for the category before the run.",
    running: "Running the allocation — this can take a minute. Keep this window open.",
    mixOverAllocated:
      "Saved space-mix targets sum to {{total}}% — the run scales them down proportionally to 100%.",
    mixUnderAllocated:
      "Every segment has a space-mix target but they sum to {{total}}% — the run scales them up to fill the shelf.",
    cancel: "Cancel",
    confirm: "Run distribution",
  },

  mixConfirm: {
    title: "Space-mix targets sum to {{total}}%",
    subtitle: "The run will not apply these numbers verbatim",
    over: "Your explicit targets add up to {{total}}%, above 100%. Saving is fine — but the distribution run scales every target down by the same factor so they fit into 100%. Relative sizes survive; the absolute numbers you typed do not, and segments without a target get no share of their own.",
    underComplete:
      "Every segment has an explicit target, but together they only add up to {{total}}%. The leftover shelf has nowhere else to flow, so the run scales your targets up proportionally to fill 100%. Relative sizes survive; the absolute numbers you typed do not.",
    hint: "To have the numbers applied as typed, adjust them to sum to 100% — or leave at least one segment without a target to absorb the difference.",
    cancel: "Back to editing",
    confirm: "Save anyway",
  },

  restoreEtl: {
    title: "Restore the ranging from the last ETL import?",
    subtitle: "Undoes every distribution run since the category's last ETL import.",
    loading: "Checking what would change…",
    loadFailed: "Could not check what a restore would change. Close and try again.",
    noFeed: "This category has no ETL feed rows, so there is no ETL ranging to restore.",
    pendingImport:
      "An ETL load for this category is waiting to be imported. It has already replaced the last imported load, so there is no imported ranging to restore to. Run the ETL import first, then restore.",
    lastImport: "Last ETL import: {{date}}",
    summary:
      "In the {{stores}} stores the feed covers, {{rangedAgain}} store/SKU rows go back into the assortment and {{unranged}} come out of it.",
    upToDate: "The ranging already matches the last ETL import. There is nothing to restore.",
    kept: "Only the ranging changes: SKUs the feed left out of a store come out of it, and stores the feed does not cover stay as they are. Min/max targets, reserved space, exclusions, space-mix targets and facings are kept; an excluded product the feed ranged is ranged again until the next run takes it out. Existing planograms change only when they are regenerated.",
    cancel: "Cancel",
    confirm: "Restore",
  },

  runResult: {
    title: "Distribution complete",
    subtitle: "Finished in {{seconds}}s. Assortments are updated in place.",
    changedSkus: "SKUs changed",
    added: "Store adds",
    removed: "Store removals",
    excludedRowsRemoved: "Of these, excluded products",
    rangedNote:
      "{{rangedRows}} ranged store/SKU rows across {{products}} SKUs and {{stores}} stores after the run.",
    skippedStores: "{{count}} stores without fixture width were skipped and left unchanged.",
    warningsTitle: "{{count}} warnings",
    noWarnings: "No warnings — every constraint was satisfied.",
    close: "Close",
  },

  toasts: {
    targetsSaved: "Targets saved",
    targetsSavedMessage: "{{count}} products updated.",
    targetsSaveFailed: "Failed to save targets",
    spaceMixSavedMessage: "{{count}} segment targets updated.",
    spaceMixSaveFailed: "Failed to save space mix targets",
    facingsSaveFailed: "Failed to save facings per SKU",
    nothingToSave: "No changes to save",
    runFailed: "Failed to run distribution",
    restored: "Ranging restored",
    restoredMessage:
      "{{rangedAgain}} store/SKU rows are back in the assortment and {{unranged}} were taken out.",
    restoreFailed: "Failed to restore the ranging",
    exportEmpty: "Nothing to export",
    exportEmptyMessage: "No products match the current filters.",
    imported: "CSV imported",
    importSummary:
      "{{count}} products updated, {{unknown}} unknown product codes, {{invalid}} invalid rows. Review the highlighted rows and save the targets.",
    importFailed: "Could not import the CSV",
    importNoKeyColumn: "The file needs a product_code column.",
  },
} as const;

export type DistributionPlannerTranslations = typeof distributionPlannerEn;
